import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import api from "../api"
import { ReactComponent as CalendlyIcon } from "../assets/images/icons/ic-calendly.svg"
import MainContext from "../common/MainContext"
import Button from "./Button"
import Select from "./Select"
import styles from "./CalendlyIntegration.module.css"

const CalendlyIntegration = () => {
  const context = useContext(MainContext)
  const [connection, setConnection] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const options = useMemo(() => (
    (connection?.event_types ?? []).map((eventType, index) => ({
      id: index,
      name: `${eventType.name}${eventType.duration ? ` · ${eventType.duration} min` : ''}`,
      value: eventType.uri,
      schedulingUrl: eventType.scheduling_url,
    }))
  ), [connection])

  const selectedEvent = useMemo(() => (
    options.find(option => option.value === connection?.selected_event_type_uri) ?? null
  ), [connection?.selected_event_type_uri, options])

  const loadConnection = useCallback(async () => {
    setLoading(true)
    setError("")
    try {
      setConnection(await api.get("/admin/calendly/connection"))
      const params = new URLSearchParams(window.location.search)
      if (params.get("calendly") === "error") {
        setError(params.get("detail") || "Collegamento Calendly non riuscito.")
      }
      if (params.has("calendly")) {
        window.history.replaceState({}, "", window.location.pathname)
      }
    } catch (e) {
      setError(e?.detail || "Non è stato possibile leggere il collegamento Calendly.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadConnection()
  }, [loadConnection])

  const connect = async () => {
    setLoading(true)
    setError("")
    try {
      const result = await api.post("/admin/calendly/oauth/start")
      window.location.assign(result.authorization_url)
    } catch (e) {
      setError(e?.detail || "Non è stato possibile avviare il collegamento Calendly.")
      setLoading(false)
    }
  }

  const selectEvent = async (option) => {
    setLoading(true)
    setError("")
    try {
      const updatedConnection = await api.patch("/admin/calendly/connection/event-type", {
        event_type_uri: option.value,
      })
      setConnection(updatedConnection)
      context.setUser({ ...context.user, calendly_url: updatedConnection.selected_scheduling_url })
    } catch (e) {
      setError(e?.detail || "Non è stato possibile selezionare l’appuntamento Calendly.")
    } finally {
      setLoading(false)
    }
  }

  const sync = async () => {
    setLoading(true)
    setError("")
    try {
      const updatedConnection = await api.post("/admin/calendly/connection/sync")
      setConnection(updatedConnection)
      context.setUser({ ...context.user, calendly_url: updatedConnection.selected_scheduling_url })
    } catch (e) {
      setError(e?.detail || "Non è stato possibile aggiornare gli appuntamenti Calendly.")
    } finally {
      setLoading(false)
    }
  }

  const disconnect = async () => {
    setLoading(true)
    setError("")
    try {
      await api.delete("/admin/calendly/connection")
      setConnection({ connected: false, event_types: [] })
      context.setUser({ ...context.user, calendly_url: null })
    } catch (e) {
      setError(e?.detail || "Non è stato possibile scollegare Calendly.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className={styles.integrationCard}>
      <div className={styles.integrationIdentity}>
        <div className={styles.icon}><CalendlyIcon /></div>
        <div>
          <h2>Calendly</h2>
          <p>
            {loading && connection === null
              ? "Verifica del collegamento in corso…"
              : connection?.connected
                ? `Collegato${connection.email ? ` come ${connection.email}` : ""}`
                : "Collega il tuo account e scegli l’appuntamento da proporre ai lead."}
          </p>
        </div>
        <span className={`${styles.connectionBadge} ${connection?.connected ? styles.connected : ""}`}>
          {loading && connection === null
            ? "Verifica…"
            : connection?.connected ? "Collegato" : "Non collegato"}
        </span>
      </div>

      {error && <div className={styles.error} role="alert">{error}</div>}

      {!connection?.connected ? (
        <div className={styles.actions}>
          <Button
            appearance="subtle"
            accentColor="var(--primary)"
            disabled={loading}
            onClick={connect}
          >
            Collega Calendly
          </Button>
        </div>
      ) : (
        <div className={styles.configuration}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>Tipo di appuntamento per i lead</span>
            <Select
              appearance="modern"
              portal
              placeholder="Seleziona un appuntamento"
              selected={selectedEvent}
              options={options}
              onSelect={selectEvent}
              disabled={loading}
              loading={loading}
            />
            {!selectedEvent && (
              <span className={styles.hint}>Seleziona un appuntamento per attivare il link nelle email.</span>
            )}
          </div>
          {connection.selected_scheduling_url && (
            <a
              className={styles.personalLink}
              href={connection.selected_scheduling_url}
              target="_blank"
              rel="noreferrer"
            >
              Apri il link personale
            </a>
          )}
          <div className={styles.actions}>
            <Button appearance="subtle" disabled={loading} onClick={sync}>
              Aggiorna appuntamenti
            </Button>
            <Button appearance="subtle" disabled={loading} onClick={disconnect}>
              Scollega
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}

export default CalendlyIntegration
